import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';

export const AuthContext = createContext();

export const useAuth = () => {
    return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(localStorage.getItem('token'));
    const [loading, setLoading] = useState(true);

    const fetchUser = useCallback(async () => {
        if (token) {
            try {
                const res = await axios.get('/api/profile/me', {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                setUser(res.data);
            } catch (error) {
                console.error('Failed to fetch user', error);
                localStorage.removeItem('token');
                setToken(null);
                setUser(null);
            }
        }
        setLoading(false);
    }, [token]);

    useEffect(() => {
        fetchUser();
    }, [fetchUser]);

    const login = async (username, password) => {
        try {
            const res = await axios.post('/api/auth/login', { username, password });
            if (res.data.token) {
                const { token, user } = res.data;
                localStorage.setItem('token', token);
                setToken(token);
                setUser(user);
                return { user };
            }
        } catch (error) {
            throw new Error(error.response?.data?.msg || 'Login Failed');
        }
    };

    const signup = async (name, username, email, password, invitationCode, mobile) => {
        try {
            const res = await axios.post('/api/auth/register', {
                name,
                username,
                email,
                password,
                invitationCode,
                mobile,
            });
            if (res.data.token) {
                const { token } = res.data;
                localStorage.setItem('token', token);
                setToken(token);
                await fetchUser(); // Fetch user profile after signup
            }
        } catch (error) {
            throw new Error(error.response?.data?.msg || error.response?.data?.message || 'Signup Failed');
        }
    };

    const logout = () => {
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
    };

    const value = {
        user,
        token,
        loading,
        login,
        signup,
        logout,
        fetchUser,
    };

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
}; 